import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Egg, HatchResult } from '../lib/types';
import { Button, Confetti, Modal, ShinyChip, TypeChip } from './ui';
import { EggArt } from './EggArt';
import { dexNumber, genderSuffix } from '../lib/format';

/**
 * The payoff moment. Two beats:
 *   1. the egg wobbles and cracks apart (1.1s)
 *   2. the Pokemon bursts out behind a starburst
 *
 * The result is already decided and fetched before this mounts — the animation
 * is presentation only, never a place where an outcome is computed.
 */
export function HatchModal({
  egg,
  result,
  onClose,
}: {
  egg: Egg;
  result: HatchResult;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<'cracking' | 'revealed'>('cracking');

  useEffect(() => {
    const timer = window.setTimeout(() => setPhase('revealed'), 1150);
    return () => window.clearTimeout(timer);
  }, []);

  const { pokemon } = result;

  return (
    <Modal onClose={phase === 'revealed' ? onClose : () => undefined} labelledBy="hatch-title">
      <div className="center stack">
        <div className="hatch-stage">
          {phase === 'cracking' ? (
            <EggArt appearance={egg.appearance} size="lg" state="hatching" label={egg.familyName} />
          ) : (
            <>
              <div className={`hatch-burst ${pokemon.isShiny ? 'hatch-burst--shiny' : ''}`} aria-hidden />
              {(pokemon.isShiny || result.isNewSpecies) && <Confetti count={pokemon.isShiny ? 26 : 14} />}
              <img className="hatch-sprite pixel" src={pokemon.sprite} alt={pokemon.species.displayName} />
            </>
          )}
        </div>

        {phase === 'cracking' ? (
          <p className="mono muted">It's hatching...</p>
        ) : (
          <div className="stack">
            <div className="mono tiny" style={{ letterSpacing: '0.18em', color: 'var(--gold-dark)' }}>
              {pokemon.isShiny ? '✦ A SHINY HATCHED ✦' : 'AN EGG HATCHED'}
            </div>

            <h2 id="hatch-title" style={{ fontSize: 'var(--text-2xl)' }}>
              {pokemon.species.displayName}
              {genderSuffix(pokemon.species.displayName, pokemon.gender) && (
                <span className="muted" style={{ marginLeft: 6 }}>
                  {genderSuffix(pokemon.species.displayName, pokemon.gender)}
                </span>
              )}
            </h2>

            <div className="row" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
              <span className="chip">{dexNumber(pokemon.species.id)}</span>
              {pokemon.species.types.map((type) => (
                <TypeChip key={type} type={type} />
              ))}
              {pokemon.isShiny && <ShinyChip />}
              {result.isNewSpecies && <span className="chip tag-legendary">NEW ENTRY!</span>}
            </div>

            {/* The journal is the point of the family system — say so out loud. */}
            {result.isNewDiscovery && result.family && (
              <Link className="pxwell tiny" to={`/journal/${result.family.key}`} style={{ padding: 8 }}>
                ◔ First <strong>{pokemon.species.displayName}</strong> you've hatched from a{' '}
                <strong>{result.family.name}</strong> — recorded in your Journal.
              </Link>
            )}

            {/* Collecting *is* contributing. The player should see the connection. */}
            {result.project && (
              <Link className="pxwell tiny" to="/community" style={{ padding: 8 }}>
                ❖ <strong>{result.project.name}</strong> +{result.project.points} — this hatch helped
                the community along.
              </Link>
            )}

            {pokemon.species.flavorText && (
              <p className="tiny muted" style={{ maxWidth: 400, margin: '0 auto' }}>
                {pokemon.species.flavorText}
              </p>
            )}

            <div className="pxwell" style={{ padding: 'var(--gap-sm)' }}>
              <div className="row" style={{ justifyContent: 'center', gap: 'var(--gap-lg)' }}>
                <Stat label="Level" value={String(pokemon.level)} />
                <Stat label="Nature" value={pokemon.nature?.name ?? '—'} />
                <Stat label="IV total" value={`${pokemon.ivTotal}/186`} />
              </div>
            </div>

            <div className="row" style={{ justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link className="btn btn--info" to={`/pokemon/${pokemon.id}`}>
                Inspect
              </Link>
              <Button variant="gold" onClick={onClose}>
                Add to collection
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="tiny muted mono" style={{ fontSize: '0.5625rem', letterSpacing: '0.08em' }}>
        {label.toUpperCase()}
      </div>
      <div className="mono">{value}</div>
    </div>
  );
}
